import { Routes } from '@angular/router';
import { _ } from '@ngx-translate/core';

import { PageInfoResolver } from '@student/core/resolvers/page-info.resolver';
import { studentRedirectGuard } from '@student/core/guards/redirect.guard';
import { ensureAdminPortalGuard } from '@admin/ensure-admin-portal.guard';
import { ensureStudentPortalGuard } from '@student/core/guards/ensure-student-portal.guard';
import { ensureInstitutionPortalGuard } from '@institution/core/ensure-institution-portal.guard';
import { ensureNoPortalGuard } from '@shared/guards/ensure-no-portal.guard';
import { ensureAuthHubGuard } from '@core/ensure-auth-hub.guard';

/**
 * Application Routes
 *
 * Data on routes can be used to attach a variety of meta, including permissions and breadcrumbs
 *
 * @see BreadcrumbDefinition in BreadcrumbsService for more info on Breadcrumbs
 */
export const routes: Routes = [
  // --- Auth Hub Routes ---
  {
    path: '',
    canMatch: [ensureAuthHubGuard],
    loadChildren: () => import('@core/auth/auth-hub.routes'),
  },

  // --- Landing Page ---
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () =>
      import('@student/pages/landing-page/landing-page.component').then(({ LandingPageComponent }) => LandingPageComponent),
    resolve: { info: PageInfoResolver },
    canActivate: [studentRedirectGuard],
    canMatch: [ensureNoPortalGuard],
    title: _('student-portal.entryPage.title'),
  },

  // --- Admin Routes ---
  {
    path: '',
    canMatch: [ensureAdminPortalGuard],
    loadChildren: () => import('@admin/admin.routes'),
  },

  // --- Institution Routes ---
  {
    path: '',
    canMatch: [ensureInstitutionPortalGuard],
    data: { breadcrumb: false },
    loadChildren: () => import('@institution/institution.routes'),
  },

  // --- Student Routes ---
  {
    path: 'student',
    canMatch: [ensureStudentPortalGuard],
    loadChildren: () => import('@student/student.routes'),
  },

  // --- Error Pages ---
  {
    path: 'access-denied',
    loadComponent: () => import('./features/error/access-denied.component').then(({ AccessDeniedComponent }) => AccessDeniedComponent),
  },
  {
    path: 'error/:status',
    loadComponent: () => import('./features/error/error.component').then(({ ErrorComponent }) => ErrorComponent),
  },
  // Auth hub catch-all: redirects to landing domain instead of falling through to tenant routes
  // (which would 404 looking up the non-existent `auth` tenant). Must precede the global wildcard,
  // after error routes so they remain reachable on the hub.
  {
    path: '**',
    canMatch: [ensureAuthHubGuard],
    loadComponent: () => import('@core/auth/pages/oidc-post-logout-redirect/oidc-post-logout-redirect.component'),
  },
  {
    path: '**',
    loadComponent: () => import('./features/error/page-not-found.component').then(({ PageNotFoundComponent }) => PageNotFoundComponent),
  }, // Wildcard route for unmatched paths
];
